'use client';

import React, { useMemo } from 'react';
import { usePriceHistory } from '@/hooks/usePriceHistory';

interface TokenSparklineProps {
  symbol: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
  className?: string;
  showGradient?: boolean;
  // Optional: pass pre-fetched data to avoid duplicate fetches
  prices?: number[];
  isUp?: boolean;
}

/**
 * TokenSparkline - Simple 7-day price line chart like CoinMarketCap
 * Uses SVG for lightweight, dependency-free rendering
 */
export default function TokenSparkline({
  symbol,
  width = 120,
  height = 40,
  strokeWidth = 1.5,
  className = '',
  showGradient = true,
  prices: externalPrices,
  isUp: externalIsUp,
}: TokenSparklineProps) {
  // Fetch price history if not provided externally
  const { prices: fetchedPrices, isUp: fetchedIsUp, loading } = usePriceHistory(
    symbol,
    { enabled: !externalPrices }
  );

  const prices = externalPrices || fetchedPrices;
  const isUp = externalIsUp !== undefined ? externalIsUp : fetchedIsUp;

  // Generate SVG path from price data
  const { pathD, gradientPathD, minPrice, maxPrice } = useMemo(() => {
    if (!prices || prices.length < 2) {
      return { pathD: '', gradientPathD: '', minPrice: 0, maxPrice: 0 };
    }

    const validPrices = prices.filter(p => p > 0);
    if (validPrices.length < 2) {
      return { pathD: '', gradientPathD: '', minPrice: 0, maxPrice: 0 };
    }

    const minPrice = Math.min(...validPrices);
    const maxPrice = Math.max(...validPrices);
    const priceRange = maxPrice - minPrice || 1;

    const padding = 2;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Generate points
    const points = validPrices.map((price, index) => {
      const x = padding + (index / (validPrices.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((price - minPrice) / priceRange) * chartHeight;
      return { x, y };
    });

    // Create line path
    const pathD = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(' ');

    // Create gradient fill path (line + bottom closure)
    const gradientPathD = pathD + 
      ` L ${points[points.length - 1].x.toFixed(2)} ${height} ` +
      `L ${points[0].x.toFixed(2)} ${height} Z`;

    return { pathD, gradientPathD, minPrice, maxPrice };
  }, [prices, width, height]);

  // Loading state
  if (loading) {
    return (
      <div 
        className={`animate-pulse bg-neutral-200 dark:bg-neutral-700 rounded ${className}`}
        style={{ width, height }}
      />
    );
  }

  // No data state
  if (!pathD) {
    return (
      <div 
        className={`flex items-center justify-center text-neutral-400 dark:text-neutral-600 ${className}`}
        style={{ width, height }}
      >
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Flat line indicating no data */}
          <line
            x1={4}
            y1={height / 2}
            x2={width - 4}
            y2={height / 2}
            stroke="currentColor"
            strokeWidth={1}
            strokeDasharray="4 2"
            opacity={0.5}
          />
        </svg>
      </div>
    );
  }

  const lineColor = isUp ? '#55C985' : '#E05C4A'; // Descout signal-green-text / alert-red-text
  const gradientId = `sparkline-gradient-${symbol}`;

  return (
    <svg 
      width={width} 
      height={height} 
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-label={`7-day price chart for ${symbol}`}
    >
      {/* Gradient definition */}
      {showGradient && (
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={lineColor} stopOpacity={0.2} />
            <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
          </linearGradient>
        </defs>
      )}

      {/* Gradient fill */}
      {showGradient && (
        <path
          d={gradientPathD}
          fill={`url(#${gradientId})`}
        />
      )}

      {/* Price line */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Compact sparkline for table/list views
 */
export function TokenSparklineCompact({
  symbol,
  className = '',
  prices,
  isUp,
}: {
  symbol: string;
  className?: string;
  prices?: number[];
  isUp?: boolean;
}) {
  return (
    <TokenSparkline
      symbol={symbol}
      width={80}
      height={24}
      strokeWidth={1.5}
      showGradient={false}
      className={className}
      prices={prices}
      isUp={isUp}
    />
  );
}
